import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import useScrollAfterReveal from './useScrollAfterReveal';

/**
 * Owns all orchestration state for the appointment setup step:
 * - Address validation state
 * - Custom time request state
 * - Auto-selection of single appointment type / single fixed location
 * - Section visibility flags
 * - Availability IDs and timezone derivation
 * - Scroll-to-section effects
 *
 * @param {Object} params — all the values the component needs
 *   (either from BookingFlowContext or from reschedule-mode props)
 */
const useAppointmentSetup = ({
  selectedAppointmentType,
  onAppointmentTypeChange,
  selectedAddress,
  onAddressChange,
  selectedLocation,
  onLocationChange,
  fixedLocationData,
  bookingPage,
  onOutsideAvailabilityChange,
  onCustomTimeRequestChange,
  isRescheduleMode,
  prePopulatedAvailabilityIds,
  rescheduleLocationInfo,
  hasFixed,
  hasMobile,
}) => {
  // Local state
  const [addressValidation, setAddressValidation] = useState({
    validationResult: null,
    isValidating: false,
    validationError: null,
    outsideAvailabilityRequested: false,
  });
  const [customTimeRequested, setCustomTimeRequested] = useState(false);

  // Refs
  const addressSectionRef = useRef(null);
  const locationSectionRef = useRef(null);
  const dateTimeSectionRef = useRef(null);
  // Set only when the customer actively picks an appointment type (via the
  // selector), never on auto-select or a context-restored value. The reveal
  // scroll consumes and clears it, so only user taps scroll the section in.
  const userSelectedTypeRef = useRef(false);
  // Set only when the customer takes an action that should reveal the date-time
  // section (picks a fixed location, or selects an address). Never set on
  // auto-select of a single location or a context-restored value, so step 2
  // opens at the top on arrival. The reveal scroll consumes it.
  const userAdvancedRef = useRef(false);

  // Scroll helper — waits for reveal animations before scrolling
  const scrollAfterReveal = useScrollAfterReveal();

  // Derived
  const hasMultipleOptions = hasFixed && hasMobile;

  // -----------------------------------------------------------------------
  // Auto-selection effects
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (hasFixed && !hasMobile && !selectedAppointmentType) {
      onAppointmentTypeChange('fixed');
    } else if (hasMobile && !hasFixed && !selectedAppointmentType) {
      onAppointmentTypeChange('mobile');
    }
  }, [hasFixed, hasMobile, selectedAppointmentType, onAppointmentTypeChange]);

  useEffect(() => {
    if (
      selectedAppointmentType === 'fixed' &&
      fixedLocationData?.locations?.length === 1 &&
      !selectedLocation
    ) {
      onLocationChange(fixedLocationData.locations[0]);
    }
  }, [
    selectedAppointmentType,
    fixedLocationData?.locations,
    selectedLocation,
    onLocationChange,
  ]);

  // -----------------------------------------------------------------------
  // Scroll effects
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!selectedAppointmentType) return;
    // Only a customer tap scrolls the revealed section into view; auto-select
    // and context-restore leave the page where it is (at the top).
    if (!userSelectedTypeRef.current) return;

    if (selectedAppointmentType === 'mobile') {
      userSelectedTypeRef.current = false;
      scrollAfterReveal(addressSectionRef);
    } else if (
      selectedAppointmentType === 'fixed' &&
      fixedLocationData?.locations?.length >= 1
    ) {
      // Consume the flag only once we actually scroll. If a user picks 'fixed'
      // before the locations list has loaded, the length guard fails, the flag
      // persists, and this effect re-runs to scroll when locations arrive.
      userSelectedTypeRef.current = false;
      scrollAfterReveal(locationSectionRef);
    }
  }, [
    selectedAppointmentType,
    fixedLocationData?.locations?.length,
    scrollAfterReveal,
  ]);

  useEffect(() => {
    setCustomTimeRequested(false);
  }, [selectedAppointmentType]);

  // -----------------------------------------------------------------------
  // Section visibility memos
  // -----------------------------------------------------------------------

  const shouldShowDateTimeSection = useMemo(() => {
    if (selectedAppointmentType === 'fixed') {
      return (
        selectedLocation || fixedLocationData?.locations?.length === 1
      );
    }
    if (selectedAppointmentType === 'mobile') {
      if (isRescheduleMode) return true;
      if (selectedAddress) {
        const isWithinArea =
          addressValidation.validationResult?.isWithinServiceArea === true;
        const isOutsideWithRequest =
          addressValidation.validationResult?.isWithinServiceArea === false &&
          addressValidation.outsideAvailabilityRequested;
        return isWithinArea || isOutsideWithRequest;
      }
    }
    return false;
  }, [
    isRescheduleMode,
    selectedAppointmentType,
    selectedLocation,
    fixedLocationData?.locations?.length,
    selectedAddress,
    addressValidation.validationResult?.isWithinServiceArea,
    addressValidation.outsideAvailabilityRequested,
  ]);

  useEffect(() => {
    if (!shouldShowDateTimeSection) return;
    // Only a customer tap (location pick or address selection) scrolls the
    // revealed section into view; auto-select, context-restore, and reschedule
    // (all of which can make this true on/after mount without a tap) leave the
    // page at the top on arrival.
    if (!userAdvancedRef.current) return;

    // For mobile, anchor on the address+travel-fee block so the fee the customer
    // just triggered stays visible; the calendar is revealed directly beneath it.
    // Fixed-location has no travel fee / address block above the calendar, so
    // scroll straight to the calendar. Consume the flag only once we actually
    // scroll: if the target section isn't mounted yet, keep the flag set so this
    // effect re-runs and scrolls when it renders.
    if (selectedAppointmentType === 'mobile' && !isRescheduleMode) {
      if (!addressSectionRef.current) return;
      userAdvancedRef.current = false;
      scrollAfterReveal(addressSectionRef);
    } else {
      if (!dateTimeSectionRef.current) return;
      userAdvancedRef.current = false;
      scrollAfterReveal(dateTimeSectionRef);
    }
  }, [
    shouldShowDateTimeSection,
    selectedAppointmentType,
    isRescheduleMode,
    scrollAfterReveal,
  ]);

  const canProceedToDateTime = useMemo(() => {
    if (selectedAppointmentType === 'fixed') {
      return (
        selectedLocation || fixedLocationData?.locations?.length === 1
      );
    }
    if (selectedAppointmentType === 'mobile') {
      if (isRescheduleMode) return true;
      return (
        selectedAddress &&
        addressValidation.validationResult?.isWithinServiceArea === true &&
        !addressValidation.isValidating
      );
    }
    return false;
  }, [
    isRescheduleMode,
    selectedAppointmentType,
    selectedLocation,
    fixedLocationData?.locations?.length,
    selectedAddress,
    addressValidation.validationResult?.isWithinServiceArea,
    addressValidation.isValidating,
  ]);

  const shouldShowOutsideAvailabilityCustomTime = useMemo(() => {
    return (
      selectedAppointmentType === 'mobile' &&
      selectedAddress &&
      addressValidation.validationResult?.isWithinServiceArea === false &&
      addressValidation.outsideAvailabilityRequested &&
      bookingPage?.allowOutsideAvailability &&
      !addressValidation.isValidating
    );
  }, [
    selectedAppointmentType,
    selectedAddress,
    addressValidation.validationResult?.isWithinServiceArea,
    addressValidation.outsideAvailabilityRequested,
    bookingPage?.allowOutsideAvailability,
    addressValidation.isValidating,
  ]);

  const showCustomTimeRequest = useMemo(() => {
    return shouldShowOutsideAvailabilityCustomTime || customTimeRequested;
  }, [shouldShowOutsideAvailabilityCustomTime, customTimeRequested]);

  const showDateTimeSelection = useMemo(() => {
    return canProceedToDateTime && !customTimeRequested;
  }, [canProceedToDateTime, customTimeRequested]);

  // -----------------------------------------------------------------------
  // Availability IDs / timezone derivation
  // -----------------------------------------------------------------------

  const availabilityIds =
    selectedAppointmentType === 'mobile'
      ? isRescheduleMode
        ? prePopulatedAvailabilityIds
        : addressValidation.validationResult?.matchingAvailabilityIds
      : selectedLocation
        ? [selectedLocation.id]
        : isRescheduleMode
          ? []
          : fixedLocationData?.locations?.length === 1
            ? [fixedLocationData.locations[0].id]
            : [];

  const timeZoneId =
    selectedAppointmentType === 'mobile'
      ? isRescheduleMode
        ? rescheduleLocationInfo?.timeZoneId
        : addressValidation.validationResult?.timeZoneId
      : selectedLocation
        ? selectedLocation.timeZoneId
        : isRescheduleMode
          ? null
          : fixedLocationData?.locations?.length === 1
            ? fixedLocationData.locations[0].timeZoneId
            : null;

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  // Wrapper for the appointment-type selector: flags the change as
  // user-initiated so the reveal-scroll effect scrolls the section into view.
  // Auto-select (the effect above) calls the raw onAppointmentTypeChange and so
  // never sets this flag.
  const handleAppointmentTypeSelect = useCallback((type) => {
    userSelectedTypeRef.current = true;
    onAppointmentTypeChange(type);
  }, [onAppointmentTypeChange]);

  // Wrappers for the fixed-location picker and the address lookup: flag the
  // change as user-initiated so the date-time reveal scroll fires. Single-location
  // auto-select (the effect above) and context-restore call the raw setters, so
  // they never set the flag and never scroll on arrival.
  const handleLocationSelect = useCallback((location) => {
    userAdvancedRef.current = true;
    onLocationChange(location);
  }, [onLocationChange]);

  const handleAddressSelect = useCallback((address) => {
    userAdvancedRef.current = true;
    onAddressChange(address);
  }, [onAddressChange]);

  const handleValidationChange = useCallback(
    (validation) => {
      setAddressValidation(validation);
      if (
        onOutsideAvailabilityChange &&
        validation.outsideAvailabilityRequested !== undefined
      ) {
        onOutsideAvailabilityChange(validation.outsideAvailabilityRequested);
      }
    },
    [onOutsideAvailabilityChange]
  );

  const handleRequestCustomTime = useCallback(() => {
    setCustomTimeRequested(true);
    if (onCustomTimeRequestChange) {
      onCustomTimeRequestChange(true);
    }
    scrollAfterReveal(dateTimeSectionRef);
  }, [onCustomTimeRequestChange, scrollAfterReveal]);

  const handleBackToTimeSlots = useCallback(() => {
    setCustomTimeRequested(false);
    if (onCustomTimeRequestChange) {
      onCustomTimeRequestChange(false);
    }
  }, [onCustomTimeRequestChange]);

  // -----------------------------------------------------------------------
  // Return
  // -----------------------------------------------------------------------

  return {
    // Refs
    addressSectionRef,
    locationSectionRef,
    dateTimeSectionRef,
    // Derived flags
    hasMultipleOptions,
    showDateTimeSelection,
    showCustomTimeRequest,
    canProceedToDateTime,
    customTimeRequested,
    // Availability
    availabilityIds,
    timeZoneId,
    // Handlers
    handleAppointmentTypeSelect,
    handleLocationSelect,
    handleAddressSelect,
    handleValidationChange,
    handleRequestCustomTime,
    handleBackToTimeSlots,
  };
};

export default useAppointmentSetup;
